import { COUNTRIES } from '@/components/input-phone-number/components/search-country/countries';
import libPhoneNumber from 'libphonenumber-js';
import { ChangeEvent } from 'react';

interface IUsePhoneQuestionArgs {
	label: string;
	name?: string;
	onChange: (e: ChangeEvent<HTMLInputElement | Element>) => void;
	onBlur?: (e: ChangeEvent<HTMLInputElement | Element>) => void;
	isLoading?: boolean;
	value: string;
	error?: string;
	isInvalid?: boolean;
}

export function usePhoneQuestion(args: IUsePhoneQuestionArgs) {

	function getDefaultCountry() {
		// If there's already a parsed phone number, use its country
		const parsed = libPhoneNumber(args.value);
		if (parsed?.country) {
			return COUNTRIES.find(c => c.code === parsed.country);
		}

		// Try to guess the country based on the browser language
		const langToCountryCode: { [key: string]: string } = {
			'es-ES': 'ES',    // Spain
			'es-MX': 'MX',    // Mexico
			'en-US': 'US',    // United States
			'en-GB': 'GB',    // United Kingdom
			'fr-FR': 'FR',    // France
			'de-DE': 'DE',    // Germany
			'it-IT': 'IT',    // Italy
			'pt-BR': 'BR',    // Brazil
			'pt-PT': 'PT',    // Portugal
			'ja-JP': 'JP',    // Japan
			'ko-KR': 'KR',    // South Korea
			'zh-CN': 'CN',    // China
			'ru-RU': 'RU',    // Russia
			'ar-SA': 'SA',    // Saudi Arabia
			'hi-IN': 'IN',    // India
		};
		
		// Check exact language-country match first
		if (langToCountryCode[navigator.language]) {
			return COUNTRIES.find(c => c.code === langToCountryCode[navigator.language]);
		}
		
		// Fallback to language only (without country)
		const lang = navigator.language.split('-')[0];
		const fallbackCountries: { [key: string]: string } = {
			'es': 'ES',   // Spanish -> Spain
			'en': 'US',   // English -> United States
			'fr': 'FR',   // French -> France
			'de': 'DE',   // German -> Germany
			'it': 'IT',   // Italian -> Italy
			'pt': 'PT',   // Portuguese -> Portugal
			'ja': 'JP',   // Japanese -> Japan
			'ko': 'KR',   // Korean -> South Korea
			'zh': 'CN',   // Chinese -> China
			'ru': 'RU',   // Russian -> Russia
			'ar': 'SA',   // Arabic -> Saudi Arabia
			'hi': 'IN',   // Hindi -> India
		};
		
		if (fallbackCountries[lang]) {
			return COUNTRIES.find(c => c.code === fallbackCountries[lang]);
		}

		// Default to United States if no match found
		return COUNTRIES.find(c => c.code === 'US');
	}

	return {
		getDefaultCountry,
	};
}

// Legacy exports for backwards compatibility (if needed elsewhere)
export const PHONE_PREFIXES = COUNTRIES.map(country => ({
	value: country.dial_code.replace('+', ''),
	label: `${country.flag} ${country.name} ${country.dial_code}`
}));
